import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { agentAssignmentService } from "../api/services/agentAssignment/agentAssignmentService";
import {
  CreateAgentAssignmentRequest,
  UpdateAgentAssignmentRequest,
  AgentAssignmentListParams,
  CreatePlayerFacilityRequest,
  PlayerFacilityListParams,
} from "../api/services/agentAssignment/types";
import toast from "react-hot-toast";

// Agent Assignment Hooks
export const useAgentAssignments = (params?: AgentAssignmentListParams) => {
  return useQuery({
    queryKey: ["agent-assignments", params],
    queryFn: () => agentAssignmentService.listAssignments(params),
  });
};

export const useAgentAssignment = (id: string, enabled = true) => {
  return useQuery({
    queryKey: ["agent-assignment", id],
    queryFn: () => agentAssignmentService.getAssignment(id),
    enabled: enabled && !!id,
  });
};

export const useCreateAgentAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAgentAssignmentRequest) =>
      agentAssignmentService.createAssignment(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-assignments"] });
      toast.success("Agent assignment created successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to create agent assignment",
      );
    },
  });
};

export const useUpdateAgentAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateAgentAssignmentRequest;
    }) => agentAssignmentService.updateAssignment(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["agent-assignments"] });
      queryClient.invalidateQueries({ queryKey: ["agent-assignment", id] });
      toast.success("Agent assignment updated successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to update agent assignment",
      );
    },
  });
};

export const useDeactivateAgentAssignment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => agentAssignmentService.deactivateAssignment(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-assignments"] });
      toast.success("Agent assignment deactivated successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(
        err.response?.data?.detail || "Failed to deactivate agent assignment",
      );
    },
  });
};

// Player Facility Hooks
export const usePlayerFacilities = (params?: PlayerFacilityListParams) => {
  return useQuery({
    queryKey: ["player-facilities", params],
    queryFn: () => agentAssignmentService.listFacilities(params),
  });
};

export const useAddPlayerFacility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreatePlayerFacilityRequest) =>
      agentAssignmentService.addFacility(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-facilities"] });
      toast.success("Facility added successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || "Failed to add facility");
    },
  });
};

export const useApproveFacility = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (facilityId: string) =>
      agentAssignmentService.approveFacility(facilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["player-facilities"] });
      toast.success("Facility approved successfully");
    },
    onError: (error: unknown) => {
      const err = error as { response?: { data?: { detail?: string } } };
      toast.error(err.response?.data?.detail || "Failed to approve facility");
    },
  });
};
